/* RC Hobby Shop — one-line scroll rows and the model hub More / Less toggle (v42).
   Loaded by snippets/rchs-scroll-rows.liquid, which also owns the CSS.
   - [data-rchs-fade]: a horizontally scrolling row. Keeps is-scrollable / is-start / is-end current, so the CSS fades
     only an edge that has more to scroll (the parts finder's model chips, the hub's spec chips, stats and part types).
     When a visitor opens a <details> in the row (the hub's "More part types"), the row scrolls so its summary and
     the newly shown chips come into view.
   - [data-rchs-more]: the More / Less button on the hub's clamped lede (sections/rchs-model-hub.liquid). The clamp is
     CSS, so nothing moves on load; a click toggles the wrapper's is-open class and the button's aria-expanded (which
     also switches its label). Focus stays on the button. The button hides itself while the lede fits in two lines.
   Idempotent, and runs again for a section re-rendered in the theme editor (shopify:section:load). */

const rows = new WeakSet();
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {HTMLElement} row */
function bindRow(row) {
  if (rows.has(row)) return;
  rows.add(row);
  const update = () => {
    const max = row.scrollWidth - row.clientWidth;
    const x = Math.abs(row.scrollLeft);
    row.classList.toggle('is-scrollable', max > 2);
    row.classList.toggle('is-start', x <= 2);
    row.classList.toggle('is-end', x >= max - 2);
  };
  row.addEventListener('scroll', update, { passive: true });
  // A summary activated by the visitor (click, Enter and Space all fire click) just before the toggle
  let clickedAt = -Infinity;
  row.addEventListener(
    'click',
    (event) => {
      if (event.target instanceof Element && event.target.closest('summary')) clickedAt = performance.now();
    },
    true
  );
  // "More part types" opens inside the hub's part-type row (toggle does not bubble, so listen in the capture phase)
  row.addEventListener(
    'toggle',
    (event) => {
      update();
      const details = event.target;
      if (performance.now() - clickedAt > 1000 || !(details instanceof HTMLDetailsElement)) return;
      clickedAt = -Infinity;
      const summary = details.querySelector(':scope > summary');
      if (!details.open || !summary) return;
      const fade = parseFloat(getComputedStyle(row).getPropertyValue('--rchs-fade-size')) || 0;
      const offset = summary.getBoundingClientRect().left - row.getBoundingClientRect().left - fade;
      row.scrollBy({ left: offset, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    },
    true
  );
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(update);
    observer.observe(row);
    if (row.firstElementChild) observer.observe(row.firstElementChild);
  } else {
    window.addEventListener('resize', update);
  }
  document.fonts?.ready.then(update);
  update();
}

/** @param {HTMLButtonElement} button */
function bindMore(button) {
  if (button.dataset.rchsBound) return;
  const wrap = button.closest('.rchs-hub__lede-wrap');
  const lede = document.getElementById(button.getAttribute('aria-controls') || '');
  if (!wrap || !lede) return;
  button.dataset.rchsBound = 'true';
  const check = () => {
    if (wrap.classList.contains('is-open')) return;
    wrap.classList.toggle('is-fits', lede.scrollHeight <= lede.clientHeight + 1);
  };
  button.addEventListener('click', () => {
    const open = !wrap.classList.contains('is-open');
    wrap.classList.toggle('is-open', open);
    button.setAttribute('aria-expanded', String(open));
    if (!open) check();
  });
  if ('ResizeObserver' in window) new ResizeObserver(check).observe(lede);
  else window.addEventListener('resize', check);
  document.fonts?.ready.then(check);
  check();
}

/** @param {Document | Element} root */
function init(root) {
  root.querySelectorAll('[data-rchs-fade]').forEach((row) => bindRow(/** @type {HTMLElement} */ (row)));
  root.querySelectorAll('[data-rchs-more]').forEach((button) => bindMore(/** @type {HTMLButtonElement} */ (button)));
}

init(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
